import type { AppStore } from "@/store"
import { currentStaffSession } from "@/store/staffSessionSlice"
import { registerStaffWriteBoundary } from "@/services/staffTransportSession"
import { createResourceRefreshCoordinator, type ObservedResource, type RefreshClock } from "@/services/realtime/resourceRefreshCoordinator"
import type { BenefitSubscriptionRegistry } from "@/services/realtime/benefitSubscriptions"
import { startBenefitBackgroundRefresh, type BenefitRefreshEnvironment } from "@/services/realtime/benefitBackgroundRefresh"
import type { benefitApi } from "./benefitApi"
import { benefitWorkspaceTag } from "./benefitEndpointDefinitions"
import { benefitReadActions, benefitReadKey } from "./benefitReadSlice"
import { workspaceCanAuthorize } from "./benefitWorkspace"

interface Handle { abort(): void; unsubscribe(): void }
interface Observer {
  resource: ObservedResource; count: number; releaseBoundary(): void
  workspace?: Handle
  releaseSubscription?: () => void
  denied?: boolean
}

/** Public RTK subscriptions/requests only. No second saved-data cache. */
export function createBenefitReadRuntime(store: AppStore, client: typeof benefitApi, options: {
  clock?: RefreshClock
  /** Explicit opt-in; live activation remains a separate readiness decision. */
  subscriptions?: BenefitSubscriptionRegistry
  background?: { environment: BenefitRefreshEnvironment; random?: () => number }
  scopeChanged?: () => void
} = {}) {
  if (options.background && !options.subscriptions) throw new Error("Background refresh requires authorized subscriptions.")
  const observed = new Map<string, Observer>()
  let unsubscribeStore: (() => void) | undefined
  let stopBackground: (() => void) | undefined
  let independentDirty = false
  let session: ReturnType<typeof currentStaffSession> = null
  const arg = (resource: ObservedResource) => ({ scopeKey: resource.scopeKey, benefitId: resource.id })
  const outcome = (error: unknown) => {
    const status = error && typeof error === "object" && "status" in error ? error.status : null
    const originalStatus = error && typeof error === "object" && "originalStatus" in error ? error.originalStatus : null
    const code = error && typeof error === "object" && "data" in error ? (error.data as { code?: string } | null)?.code : null
    return code === "staff-scope-changed" || [401, 403, 404].includes(Number(status === "PARSING_ERROR" ? originalStatus : status))
      ? "accessDenied" as const : "unavailable" as const
  }
  const coordinator = createResourceRefreshCoordinator({
    read: async (resource, signal) => {
      const entry = observed.get(benefitReadKey(resource))
      if (!entry) return "unavailable"
      if (independentDirty) {
        independentDirty = false
        // Public RTK invalidation refetches only active independent readers, evicts
        // inactive ones, and never asserts coherence with the workspace snapshot.
        store.dispatch(client.util.invalidateTags([{ type: "BenefitIndependentRead", id: resource.scopeKey }]))
        for (const cached of client.util.selectInvalidatedBy(store.getState(), [{ type: "BenefitWorkspace" }])) {
          const args = cached.originalArgs as { scopeKey: string; benefitId: string }
          if (args.scopeKey === resource.scopeKey && !observed.has(benefitReadKey({ scopeKey: args.scopeKey, id: args.benefitId }))) {
            store.dispatch(client.util.invalidateTags([benefitWorkspaceTag(args)]))
          }
        }
      }
      if (options.subscriptions) {
        // RTK forceRefetch still shares an already-running request. A pre-ack GET may finish,
        // but only a request actually started after the ack can reconcile this generation.
        const previous = store.dispatch(client.util.getRunningQueryThunk("getBenefitWorkspace", arg(resource)))
        if (previous) {
          let cancelWait = () => {}
          const cancelled = new Promise<void>(resolve => { cancelWait = resolve })
          signal.addEventListener("abort", cancelWait, { once: true })
          try { if (!signal.aborted) await Promise.race([previous, cancelled]) }
          finally { signal.removeEventListener("abort", cancelWait) }
        }
        if (signal.aborted || observed.get(benefitReadKey(resource)) !== entry) return "unavailable"
      }
      const request = store.dispatch(client.endpoints.getBenefitWorkspace.initiate(arg(resource), {
        subscribe: !entry.workspace, forceRefetch: true,
      }))
      entry.workspace ??= request
      const abort = () => { request.abort() }
      signal.addEventListener("abort", abort, { once: true })
      if (signal.aborted) abort()
      try {
        const result = await request
        if (result.error) return outcome(result.error)
        if (!result.data || signal.aborted) return "unavailable"
        return workspaceCanAuthorize(result.data) ? "success" : "unavailable"
      } finally { signal.removeEventListener("abort", abort) }
    },
    changed: metadata => { store.dispatch(benefitReadActions.changed(metadata)) },
    removed: resource => { store.dispatch(benefitReadActions.removed(resource)) },
    invalidateInactive: resource => { store.dispatch(client.util.invalidateTags([benefitWorkspaceTag(arg(resource))])) },
    accessDenied: resource => {
      // Clear protected bodies without reducer-internal eviction or an active-query
      // invalidation loop. The page unmounts its editor subtree on denial.
      const entry = observed.get(benefitReadKey(resource))
      if (entry) { entry.denied = true; entry.releaseSubscription?.(); entry.releaseSubscription = undefined }
      store.dispatch(client.util.updateQueryData("getBenefitWorkspace", arg(resource), () => null))
    },
  }, { clock: options.clock })

  function writable(resource: ObservedResource) {
    const state = store.getState()
    const current = currentStaffSession(state)
    // injectEndpoints adds definitions/tags to the existing reducer at runtime;
    // the root store's static base-api type cannot know every injected feature.
    const queryState = state as typeof state & { api: ReturnType<typeof client.reducer> }
    return !!current && current.scopeKey === resource.scopeKey && current.epoch === session?.epoch
      && state.benefitReads.resources[benefitReadKey(resource)]?.phase === "current"
      && client.endpoints.getBenefitWorkspace.select(arg(resource))(queryState).status === "fulfilled"
      && workspaceCanAuthorize(client.endpoints.getBenefitWorkspace.select(arg(resource))(queryState).data)
  }
  // One runtime owns local success, joined/reconnected reads and optional background triggers.
  function releaseHandles(entry: Observer) {
    entry.releaseBoundary()
    entry.releaseSubscription?.()
    entry.workspace?.unsubscribe()
  }
  function teardown() {
    stopBackground?.(); stopBackground = undefined; independentDirty = false
    unsubscribeStore?.(); unsubscribeStore = undefined
    coordinator.setScope(null)
    for (const entry of observed.values()) releaseHandles(entry)
    observed.clear()
    session = null
  }
  return {
    observe(resource: ObservedResource) {
      const current = currentStaffSession(store.getState())
      if (!current || current.scopeKey !== resource.scopeKey) throw new Error("Resource scope is not active.")
      session = current
      coordinator.setScope(resource.scopeKey)
      options.subscriptions?.setScope(resource.scopeKey, current.epoch)
      const key = benefitReadKey(resource)
      let entry = observed.get(key)
      if (entry) entry.count++
      else {
        entry = { resource, count: 1, releaseBoundary: registerStaffWriteBoundary(
          `/api/benefitapplications/${encodeURIComponent(resource.id)}`, () => writable(resource)),
        }
        observed.set(key, entry)
      }
      const owned = entry
      const release = coordinator.watch(resource)
      if (options.subscriptions && !entry.releaseSubscription && !entry.denied) {
        const registry = options.subscriptions
        try {
          entry.releaseSubscription = registry.observe({ kind: resource.kind, id: resource.id }, state => {
            if (observed.get(key) !== owned || owned.denied) return
            if (state === "joined") coordinator.resume(resource)
            else {
              coordinator.suspend(resource, state === "accessDenied" || state === "scopeChanged")
              if (state === "scopeChanged") options.scopeChanged?.()
            }
          })
        } catch {
          // Invalid targets/cap overflow fail closed without allocating another subscription.
          coordinator.suspend(resource)
        }
      }
      if (options.background && !stopBackground) {
        stopBackground = startBenefitBackgroundRefresh({
          subscriptions: options.subscriptions!, resources: () => [...observed.values()].map(item => item.resource),
          dirty: coordinator.dirty, visible: coordinator.setVisible,
          independentReadsChanged: () => { independentDirty = true }, scopeChanged: () => options.scopeChanged?.(),
        }, { ...options.background, clock: options.clock })
      }
      unsubscribeStore ??= store.subscribe(() => {
        const next = currentStaffSession(store.getState())
        if (!next || next.scopeKey !== session?.scopeKey || next.epoch !== session?.epoch) {
          options.subscriptions?.setScope(null)
          teardown()
        }
      })
      let released = false
      return () => {
        if (released) return
        released = true
        release()
        if (observed.get(key) === owned && --owned.count === 0) { releaseHandles(owned); observed.delete(key) }
        if (observed.size === 0) teardown()
      }
    },
    refresh(resource: ObservedResource) {
      options.subscriptions?.retry({ kind: resource.kind, id: resource.id })
      coordinator.dirty(resource)
    },
    dispose: teardown,
    counts: coordinator.counts,
  }
}
